import './Form.scss'
import { useForm } from 'react-hook-form'
import { Field, Input, Select, Textarea } from '@fluentui/react-components'
import { useDotnetClient } from '@/contexts/DotnetClientContext'
import { Button } from '../Button'
import { toast } from 'react-toastify'
import { useEffect } from 'react'
import { RestSchemas } from '@/contexts/QueryProvider'

type AppTypes = RestSchemas['DataModellingPlatform.Common.AppTypes']
type MessagesPipelinesNewPipeline = RestSchemas['Messages.Pipeline.NewPipeline']
type MessagesPipelinesExistingPipeline = RestSchemas['Messages.Pipeline.ExistingPipeline']

type AddPipelineFormProps = {
  onSubmit: () => void
  edit?: boolean
  selectedPipeline?: MessagesPipelinesExistingPipeline
  applicationId: number
  mode: 'pipeline' | 'definition'
}

type TPipelineForm = {
  applicationId: number
  id?: number
  type: AppTypes
  name: string
  description: string
  locked: boolean
  definition: string
}

export const AddPipelineForm = (props: AddPipelineFormProps) => {
  const { dmpClient, isDMPClientLoaded } = useDotnetClient()

  const {
    register,
    formState: { errors },
    reset,
    handleSubmit,
  } = useForm<TPipelineForm>({
    defaultValues:
      props.edit === true
        ? ({
            id: props.selectedPipeline?.id || undefined,
            applicationId: props.applicationId,
            type: props.selectedPipeline?.type || '',
            name: props.selectedPipeline?.name || '',
            description: props.selectedPipeline?.description || '',

            definition: props.selectedPipeline?.definition || '{}',
          } as TPipelineForm)
        : ({
            applicationId: props.applicationId,
            type: 0,
            name: '',
            description: '',
            locked: false,
            definition: '{}',
          } as TPipelineForm),
  })

  useEffect(() => {
    if (isDMPClientLoaded) {
      reset()
    }
  }, [isDMPClientLoaded, reset])

  const onSubmit = (data: TPipelineForm) => {
    if (props.edit && props.selectedPipeline) {
      const updatedPipeline: MessagesPipelinesExistingPipeline = {
        id: data.id!,
        ...data,
        applicationId: props.applicationId,
      }

      dmpClient?.pipelineApi
        .postPipelineApiPostPipeline(props.applicationId, updatedPipeline)
        .then(() => {
          toast.success('Pipeline successfully updated')
          props.onSubmit()
          reset()
        })
        .catch((err) => {
          console.error(err)
          toast.error('Error updating pipeline')
          props.onSubmit()
        })
    } else {
      const newPipeline: MessagesPipelinesNewPipeline = {
        ...data,
        applicationId: props.applicationId,
      }

      dmpClient?.pipelineApi
        .putPipelineApiAddPipeline(props.applicationId, newPipeline)
        .then(() => {
          toast.success('Pipeline successfully added')
          props.onSubmit()
          reset()
        })
        .catch((err) => {
          console.error(err)
          toast.error('Error adding pipeline')
          props.onSubmit()
        })
    }
  }

  return (
    <div className="form-wrapper">
      <div>
        {props.mode == 'pipeline' && (
          <>
            {/* Name Field */}
            <Field
              label="Name"
              required
              validationState={errors.name ? 'error' : 'none'}
              validationMessage={errors.name?.message}
            >
              <Input
                {...register('name', {
                  required: 'Name is required',
                  maxLength: 60,
                  setValueAs: (value) => value ?? undefined,
                })}
              />
            </Field>

            {/* Description Field */}
            <Field
              label="Description"
              validationState={errors.description ? 'error' : 'none'}
              validationMessage={errors.description?.message}
            >
              <Input
                {...register('description', {
                  maxLength: 256,
                  setValueAs: (value) => value ?? undefined,
                })}
              />
            </Field>

            {/* Type Field */}
            <Field
              label="Type"
              validationState={errors.type ? 'error' : 'none'}
              validationMessage={errors.type?.message}
            >
              <Select
                {...register('type', {
                  required: 'Type is required',
                  maxLength: 256,
                })}
              >
                <option key={'1'} value={1}>
                  Databricks
                </option>
                <option key={'2'} value={2}>
                  Dagster
                </option>
                <option key={'4'} value={4}>
                  Jupyter Hub
                </option>
              </Select>
            </Field>
          </>
        )}
        {props.mode == 'definition' && (
          <>
            <Field
              label="Definition (JSON)"
              validationState={errors.definition ? 'error' : 'none'}
              validationMessage={errors.definition?.type === 'maxLength' ? 'Max length exceeded' : undefined}
            >
              <Textarea {...register('definition')} rows={10} />
            </Field>
          </>
        )}
      </div>

      {/* Submit Button */}
      <div className="form-action-container">
        <Button text="Submit" onClick={handleSubmit(onSubmit)} />
      </div>
    </div>
  )
}
